import * as React from "react";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { ILOBCheckbox, TypeMenuProps } from "../typings";
import { UitkPill } from "uitk-react-pill";
import { FlightTypeAdaptiveMenu } from "components/flexComponents/WizardFlightPWA/components/FlightTypeAdaptiveMenu";
import { FlightType } from "stores/wizard/state/typings";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { UitkSpacing } from "uitk-react-spacing";

interface TypeMenuPillsProps extends TypeMenuProps {
  showMulticity: boolean;
  showOneway: boolean;
  setFlightType: () => string;
  onFlightTypeUpdate: (flightType: FlightType) => void;
  showEnhancedLabels: boolean;
}

export const TypeMenuPills = ({
  handleMenuChange,
  onFlightTypeUpdate,
  lobsMap,
  setFlightType,
  showMulticity,
  showOneway,
  className,
  useLink,
  showEnhancedLabels,
  lobKeys,
}: TypeMenuPillsProps) => {
  const flightsMapKey = "flights";
  /**
   * Handle flight type selection when adaptive menu is available
   * See: https://abacus.prod.expedia.com/experiment/38435
   */
  const onSelectFlightType = (flighType: FlightType) => {
    const { ref, id } = lobsMap.flights;
    const pillState = true;

    handleMenuChange(flightsMapKey, ref, id, pillState);
    onFlightTypeUpdate(flighType);
  };

  /** Unselect flight pill when adaptive menu clear button is clicked */
  const unselectFlightPill = () => {
    const { ref, id } = lobsMap.flights;
    const pillState = false;

    handleMenuChange(flightsMapKey, ref, id, pillState);
  };

  const renderFlightPillWithAdaptiveMenu = () => {
    const { checked, label: pillLabel } = lobsMap.flights;
    const currentFlightType = setFlightType();
    const label = checked ? `${pillLabel}: ${currentFlightType}` : pillLabel;
    const flightTypeSelectorInPillExperimentName = "newFlightsinPill";

    return (
      <Experiment name={flightTypeSelectorInPillExperimentName} key={flightsMapKey}>
        <ExperimentControl>{renderPill(flightsMapKey, lobsMap.flights)}</ExperimentControl>
        <ExperimentVariant bucket={1}>
          <div>
            <FlightTypeAdaptiveMenu
              onSelectFlightType={onSelectFlightType}
              onClearButtonClick={unselectFlightPill}
              initialFlightType={currentFlightType}
              trigger={
                <UitkPill id={`package-pills-${flightsMapKey}`} toggleState={checked} children={label} triggerPill />
              }
              showMulticity={showMulticity}
              showOneway={showOneway}
            />
          </div>
        </ExperimentVariant>
      </Experiment>
    );
  };

  const renderPill = (key: string, { checked, ref, id, label, enhancedLabel }: ILOBCheckbox) => {
    const overridenLabel = checked && showEnhancedLabels ? enhancedLabel : label;

    const onChangePill = () => {
      handleMenuChange(key, ref, id);
    };

    return (
      <UitkPill
        id={!useLink ? `package-pills-${key}` : ""}
        checked={checked}
        onChange={onChangePill}
        children={overridenLabel}
      />
    );
  };

  const checkLOBAddedExperimentLabels = () => {
    lobsMap.cars.label = lobsMap.cars.enhancedVariantLabel;
    lobsMap.hotels.label = lobsMap.hotels.enhancedVariantLabel;
    lobsMap.flights.label = lobsMap.flights.enhancedVariantLabel;
  };

  const pillsToLoad = () => {
    if (showEnhancedLabels) {
      checkLOBAddedExperimentLabels();
    }

    return lobKeys.map((lobKey) => {
      const lobCheckbox: ILOBCheckbox = lobsMap[lobKey];
      const pillToLoad = renderPill(lobKey, lobCheckbox);
      const adaptiveMenuPill = renderFlightPillWithAdaptiveMenu();
      const adaptiveMenuLinkPill = (
        <UitkSpacing margin={{ inlinestart: "one", inlineend: "one" }}>
          <button>{pillToLoad}</button>
        </UitkSpacing>
      );

      const typePillToLoad =
        flightsMapKey === lobKey && (showMulticity || showOneway)
          ? adaptiveMenuPill
          : useLink
          ? adaptiveMenuLinkPill
          : pillToLoad;

      return <UitkLayoutFlexItem key={lobKey}>{typePillToLoad}</UitkLayoutFlexItem>;
    });
  };

  return (
    <UitkLayoutFlexItem className={className}>
      <div>
        <UitkLayoutFlex dataTestId="type-menu-pills">{pillsToLoad()}</UitkLayoutFlex>
      </div>
    </UitkLayoutFlexItem>
  );
};
